import axios, { AxiosHeaders } from 'axios';
import { analytics, logger } from './analytics';
import { refreshAccessToken, isTokenExpiringSoon } from './auth';
import { setAuthToken, setRefreshToken, clearAllTokens, getAuthToken, getRefreshToken } from './token-manager';
import { isNetworkError, SessionExpiredError, isSessionExpiredError } from '../utils/errors';
import { AUTH_CONSTANTS } from '../utils/auth-constants';
import { isProtectedRoute, isJobDetailRoute } from '../utils/route-utils';
import { router } from '../../Routes';

let interceptorsConfigured = false;

const navigateToLogin = (): void => {
  if (typeof window === 'undefined') return;
  if (!isProtectedRoute(window.location.pathname)) return;

  if (isJobDetailRoute(window.location.pathname)) {
    sessionStorage.setItem(AUTH_CONSTANTS.SESSION_STORAGE.REDIRECT_FROM_OFFERS, 'true');
  }

  if (router?.navigate) {
    router.navigate('/login');
    return;
  }

  window.location.href = '/login';
};

export const setupAxiosInterceptors = (): void => {
  if (interceptorsConfigured) return;
  interceptorsConfigured = true;

  axios.interceptors.request.use(config => {
    const authToken = getAuthToken();
    if (authToken && !config.headers?.Authorization) {
      const headers = AxiosHeaders.from(config.headers);
      headers.set('Authorization', `Bearer ${authToken}`);
      config.headers = headers;
    }
    return config;
  });

  axios.interceptors.response.use(
    response => response,
    function (error) {
      const requestUrl = error.config?.url || '';
      const isRefreshRequest = requestUrl.includes('/api/token/refresh/');
      const isAuthEndpoint = requestUrl.includes('/api/token/') || requestUrl.includes('/api/auth/');
      const authToken = getAuthToken();
      const isAccessTokenExpired = authToken ? isTokenExpiringSoon(authToken, 0) : false;

      // Rastrear erros de API, EXCETO erros esperados de autenticação
      if (error.response) {
        // Verificar se é um erro esperado de refresh token expirado
        const isExpectedAuthError = 
          error.response.status === 401 && 
          (isRefreshRequest || isAuthEndpoint || isAccessTokenExpired);
        
        // Apenas logar erros não esperados
        if (!isExpectedAuthError) {
          logger.error('API Error', undefined, {
            status: error.response.status,
            statusText: error.response.statusText,
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            responseData: error.response.data
          });
        } else {
          // Log silencioso para erros esperados de autenticação
          logger.auth('Token expirado ou inválido', {
            url: requestUrl,
            status: error.response.status,
            detail: error.response.data?.detail
          });
        }
        
        // Construir mensagem de erro de forma segura
        const errorMessage = (() => {
          try {
            if (error.response?.data?.message) {
              return error.response.data.message;
            }
            if (error.response?.data && typeof error.response.data === 'string') {
              return error.response.data;
            }
            if (error.response?.statusText) {
              return error.response.statusText;
            }
            return 'Unknown error';
          } catch (e) {
            return 'Unknown error';
          }
        })();

        // Adicionar contexto e rastrear erros APENAS para não esperados
        if (error.response.status === 401 && !isExpectedAuthError) {
          analytics.addBreadcrumb(
            `Unauthorized request to ${error.config?.url}`, 
            'api_error', 
            'error'
          );
          
          analytics.addBreadcrumb(
            `401 Error Details: ${errorMessage}`, 
            'authentication_error', 
            'error'
          );
          
          analytics.trackError(
            new Error(`Erro de API: ${error.response.status} - ${errorMessage}`),
            `API_${error.config?.method?.toUpperCase()}_${error.config?.url}`
          );
        } else if (!isExpectedAuthError) {
          // Rastrear outros erros não esperados
          analytics.trackError(
            new Error(`Erro de API: ${error.response.status} - ${errorMessage}`),
            `API_${error.config?.method?.toUpperCase()}_${error.config?.url}`
          );
        }
      } else if (error.request) {
        // Verificar se é um erro de rede esperado (durante refresh de token ou operações críticas)
        const isNetworkErrorExpected = isNetworkError(error) || isRefreshRequest;
        
        // Log do erro de rede
        logger.error('Network Error', undefined, {
          message: 'No response received',
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          isRefreshRequest: isRefreshRequest,
          isExpected: isNetworkErrorExpected
        });
        
        // Apenas reportar ao Sentry se não for um erro esperado de rede
        // Erros de rede durante refresh de token são temporários e não devem ser reportados
        if (!isNetworkErrorExpected) {
          analytics.trackError(
            new Error('Erro de rede: nenhuma resposta recebida'),
            'NETWORK_ERROR'
          );
        } else {
          // Log silencioso para erros de rede esperados (não reportar ao Sentry)
          analytics.addBreadcrumb(
            'Network error during token refresh - not reporting to Sentry',
            'network',
            'warning'
          );
        }
      } else {
        logger.error('Request Setup Error', error, {
          message: error.message
        });
      }

      if (error.response && error.response.status === 401) {
        const originalRequest = error.config;
        const requestUrl = originalRequest.url || '';
        const isAuthEndpoint = requestUrl.includes('/api/token/') || requestUrl.includes('/api/auth/');
        const isRefreshRequest = requestUrl.includes('/api/token/refresh/');
        const hasRefreshToken = !!getRefreshToken();
        
        // Verificar se é erro de refresh token inválido
        const isRefreshTokenError = isRefreshRequest && 
          error.response.data?.detail === "No active account found with the given credentials";
        
        if (isRefreshTokenError) {
          // Log silencioso para refresh token inválido
          logger.auth('Refresh token inválido, redirecionando silenciosamente para login', {
            reason: 'invalid_refresh_token',
            timestamp: new Date().toISOString()
          });
          
          // Limpar tokens silenciosamente
          clearAllTokens();
          
          // Redirecionar silenciosamente para login
          navigateToLogin();
          
          // Retornar erro silencioso para não mostrar mensagem técnica (não será reportado ao Sentry)
          return Promise.reject(new SessionExpiredError('Sessão expirada. Faça login novamente.', 'invalid_refresh_token'));
        }
        
        // Tentar renovar o token se não foi uma tentativa de renovação,
        // não for endpoint de autenticação (login/OTP/etc) e existir refresh token
        if (!originalRequest._retry && !isRefreshRequest && !isAuthEndpoint && hasRefreshToken) {
          originalRequest._retry = true;
          
          return refreshAccessToken().then(newTokens => {
            // Obter o valor de remember_me para manter a persistência correta
            const rememberMe = localStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.REMEMBER_ME) === 'true';
            
            // Atualizar tokens usando as funções centralizadas (mantém o storage correto)
            setAuthToken(newTokens.access, rememberMe);
            // Alguns backends não retornam refresh no endpoint de refresh.
            // Preservar o refresh existente se não houver rotação.
            if (newTokens.refresh) {
              setRefreshToken(newTokens.refresh, rememberMe);
            }
            
            // Atualizar header de autorização da requisição original
            originalRequest.headers.Authorization = `Bearer ${newTokens.access}`;
            
            // Tentar a requisição original novamente
            return axios(originalRequest);
          }).catch(refreshError => {
            // Sessão expirada é um comportamento esperado, não um erro crítico
            if (isSessionExpiredError(refreshError)) {
              // Limpar todos os tokens
              clearAllTokens();
              
              // Redirecionar para login se estiver em rota protegida
              navigateToLogin();
              
              // Retornar erro amigável (não será reportado ao Sentry)
              return Promise.reject(refreshError);
            }
            
            // Verificar se é erro de rede usando helper function centralizada
            if (isNetworkError(refreshError)) {
              // Para erros de rede, não limpar tokens - pode ser problema temporário
              // A requisição original falhará com o erro de rede, mas os tokens permanecem válidos
              logger.error('Erro de rede ao renovar token no interceptor', refreshError as Error, {
                isNetworkError: true,
                preserveTokens: true,
                httpStatus: (refreshError as any)?.httpStatus,
                code: (refreshError as any)?.code,
                skipSentry: true // Não reportar ao Sentry - erro temporário esperado
              });
              
              // Rejeitar com o erro de rede original para que a requisição original também falhe
              // mas sem limpar tokens
              return Promise.reject(refreshError);
            }
            
            // Para outros erros de autenticação, limpar tokens e redirecionar
            logger.error('Erro ao renovar token no interceptor', refreshError as Error, {
              isNetworkError: false,
              preserveTokens: false
            });
            
            // Limpar todos os tokens
            clearAllTokens();
            
            // Redirecionar para login se estiver em rota protegida
            navigateToLogin();
            
            // Retornar erro amigável
            return Promise.reject(new SessionExpiredError('Sessão expirada. Faça login novamente.', 'interceptor_error'));
          });
        }
        
        // Se for endpoint de autenticação ou não houver refresh token, não tentar renovar
        // para preservar a mensagem original de credenciais inválidas
        if (isAuthEndpoint || !hasRefreshToken) {
          return Promise.reject(error);
        }

        // Limpar todos os tokens e dados do usuário
        clearAllTokens();
        
        navigateToLogin();
        sessionStorage.clear();
        
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );
};

export const resetAxiosInterceptorsForTests = (): void => {
  interceptorsConfigured = false;
};

export default axios;
